import { isPlatformBrowser, isPlatformServer } from "@angular/common";
import { inject, PLATFORM_ID } from "@angular/core";
import { CanActivateFn } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { SESSION_EXPIRES_COOKIE } from "../../shared/utils/token-encoding.utils";
import { AuthService } from "../services/auth.service";

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const cookieService = inject(CookieService);

  if (isPlatformBrowser(platformId)) {
    if (authService.getAccessToken()) return true;

    // The token cookies are HttpOnly, so script cannot tell from them whether
    // there is a session; the marker the server sets beside them can.
    if (!cookieService.check(SESSION_EXPIRES_COOKIE)) {
      if (!state.url.startsWith("/auth/login")) {
        // Deep link or bookmark with no session: log in, then come back here.
        authService.login(state.url);
        return false;
      }
      return true;
    }

    // A page that has just loaded holds no token yet: take the browser's.
    return authService.restoreAccessToken().pipe(
      switchMap((accessToken) => {
        if (!accessToken) {
          authService.login(state.url);
          return of(false);
        }
        if (authService.isUserDataLoaded()) return of(true);

        // Cold load (new tab / bookmark / pasted URL): sessionStorage is
        // empty so the profile + permissions load asynchronously. Wait for
        // that to finish before allowing activation — otherwise the child
        // permission guard evaluates against an empty permission set and
        // bounces a deep link that the user is actually allowed to see.
        return authService.getProfileAndMenu().pipe(map(() => true));
      }),
    );
  } else if (isPlatformServer(platformId)) {
    const encodedToken = cookieService.get("access_token");
    if (!encodedToken && !state.url.startsWith("/auth/login")) {
      return false;
    }
  }
  return true;
};
